import { createContext, useState, ReactNode, useMemo, useCallback } from 'react'
import { useUserOrganizationQuery } from '@/gql/systemApi'
import { USER_ROLES } from '@/constants'
import { IS_LOGGED_IN } from '@/apollo/queries'
import { useQuery } from '@apollo/client'
import useGetRoleRestrictions from './useGetRoleRestrictions'

export interface RoleRestrictionsContextType {
  checkAndBlock: (action?: () => void | Promise<void>) => Promise<void>
  shouldBlock: boolean
  modalOpen: boolean
  openModal: () => void
  closeModal: () => void
  isLoading: boolean
  hasError: boolean
  errorMessage?: string
  refetch: () => void
  isRestrictedUser: boolean
}

const initialContextValue: RoleRestrictionsContextType = {
  checkAndBlock: () => Promise.resolve(),
  shouldBlock: false,
  modalOpen: false,
  openModal: () => {},
  closeModal: () => {},
  isLoading: false,
  hasError: false,
  refetch: () => {},
  isRestrictedUser: false,
}

export const RoleRestrictionsContext =
  createContext<RoleRestrictionsContextType>(initialContextValue)

interface RoleRestrictionsProviderProps {
  children: ReactNode
}

const RestrictionsProvider = ({ children }: RoleRestrictionsProviderProps) => {
  const [modalOpen, setModalOpen] = useState(false)
  const { data: authData } = useQuery(IS_LOGGED_IN)
  const isLoggedIn = authData?.isLoggedIn

  const {
    data: userMe,
    loading: userMeLoading,
    error: userMeError,
    refetch: refetchUserMe,
  } = useUserOrganizationQuery({
    skip: !isLoggedIn,
  })

  const isCaseManager =
    userMe?.users_me?.role?.id === USER_ROLES.CaseManagerUser
  const hasUserData = !userMeLoading && userMe?.users_me

  const {
    isLoading,
    shouldBlock,
    hasError,
    errorMessage,
    refetch,
    isRestrictedUser,
  } = useGetRoleRestrictions({
    userMe,
    userMeLoading,
    userMeError,
    refetchUserMe,
  })

  const shouldUseFullRestrictions =
    isLoggedIn && hasUserData && (isCaseManager || isRestrictedUser)

  const openModal = useCallback(() => setModalOpen(true), [])
  const closeModal = useCallback(() => setModalOpen(false), [])

  const checkAndBlock = useCallback(
    async (action?: () => void | Promise<void>): Promise<void> => {
      // If not a case manager, execute the action
      if (!shouldUseFullRestrictions) {
        await action?.()
        return
      }

      // For case managers, use the full restriction logic
      if (isLoading || hasError || shouldBlock) {
        openModal()
        return
      }

      await action?.()
    },
    [shouldUseFullRestrictions, isLoading, hasError, shouldBlock, openModal]
  )

  const contextValue = useMemo(
    () => ({
      checkAndBlock,
      shouldBlock: shouldUseFullRestrictions ? shouldBlock : false,
      modalOpen,
      openModal,
      closeModal,
      isLoading: shouldUseFullRestrictions ? isLoading : false,
      hasError: shouldUseFullRestrictions ? hasError : false,
      errorMessage: shouldUseFullRestrictions ? errorMessage : undefined,
      refetch: shouldUseFullRestrictions ? refetch : () => {},
      isRestrictedUser,
    }),
    [
      checkAndBlock,
      shouldUseFullRestrictions,
      isLoading,
      hasError,
      shouldBlock,
      errorMessage,
      refetch,
      modalOpen,
      openModal,
      closeModal,
      isRestrictedUser,
    ]
  )

  return (
    <RoleRestrictionsContext.Provider value={contextValue}>
      {children}
    </RoleRestrictionsContext.Provider>
  )
}

export const RoleRestrictionsProvider = ({
  children,
}: RoleRestrictionsProviderProps) => (
  <RestrictionsProvider>{children}</RestrictionsProvider>
)
